import { useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Button } from '@bwoty-web/ui-kit';
import styled from 'styled-components';
import PaxSelectors from './PaxSelectors';
import ExtraInfo from './ExtraInfo';

const TableData = styled.td`
  &:nth-of-type(n) {
    border-top: 0;

    /* Mobile only */
    @media (max-width: 767px) {
      > div:first-child {
        display: block;
        padding: var(--space-400);
        padding-bottom: var(--space-500);
      }
    }
    /* Desktop only */
    @media (min-width: 768px) {
      > div:first-child {
        display: flex;
        flex-direction: row;
        padding: var(--space-500);
      }
    }
  }
`;

const Search = styled.div`
  /* Desktop only */
  @media (min-width: 768px) {
    flex: 2;
    max-width: 420px;
  }
`;

const MembershipQuery = styled.div`
  margin-bottom: 1.5rem;
  font-size: 14px;
`;

const ExtraHotelNights = styled.div`
  padding: var(--space-300) var(--space-400);
  background: var(--color-grey-25);
  p {
    color: var(--color-grey-700);
    font-size: 14px;
    line-height: 18px;
  }
  /* Desktop only */
  @media (min-width: 768px) {
    padding: var(--space-400) var(--space-1000) var(--space-400) var(--space-500);
  }
`;

const FixtureBooking = ({
  fixture,
  labels,
  selectedFixture,
  siteSettings,
  travelAges,
  handleTravelAges,
  isMember,
  handleMembership,
}) => {
  const [missingPaxAge, updateMissingPaxAge] = useState(false);
  const [errorsInSearch, setErrorsInSearch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { bookingTexts, teams } = labels;
  const { siteId, hotelListPageUrl, team } = siteSettings;
  const packageType = selectedFixture.length ? selectedFixture.slice(5) : 'base';
  const { countryID, resortID, membershipText, membershipRequired } = teams[team];
  const { from, to, status, info } = fixture[packageType];

  function errorCheck() {
    // Check travelAges
    if (travelAges.includes('-1')) {
      updateMissingPaxAge(true);
      setErrorsInSearch(true);
      return false;
    }
    // Check consent
    if (!isMember && membershipRequired) {
      setErrorsInSearch(true);
      return false;
    }
    setErrorsInSearch(false);
    return true;
  }

  const searchFixture = () => {
    const isValidSearch = errorCheck();
    if (isValidSearch) {
      setIsLoading(true);
      const params = {
        checkinDate: from.replaceAll('-', ''),
        checkoutDate: to.replaceAll('-', ''),
        travelAges: travelAges.join(),
      };
      if (!hotelListPageUrl) {
        throw new Error('Config is missing Hotel list page URL (hotelListPageUrl).');
      }
      let hostname = '';
      switch (siteId) {
        case 3:
          hostname = 'ving.no';
          break;
        case 11:
          hostname = 'spies.dk';
          break;
        case 15:
          hostname = 'tjareborg.fi';
          break;
        case 1:
        default:
          hostname = 'ving.se';
          break;
      }
      const hotelListSearchURL =
        `https://${hostname}${hotelListPageUrl}?QueryDepID=-1&QueryCtryID=${countryID}&QueryAreaID=0&QueryResID=${resortID}` +
        `&QueryDestID=0&QueryChkInDate=${params.checkinDate}&QueryChkOutDate=${params.checkoutDate}&QueryDepDate=00010101&QueryRetDate=00010101` +
        `&QueryDur=NaN&CategoryId=6&QueryRoomAges=|${params.travelAges}&QueryUnits=1`;
      window.location.href = hotelListSearchURL;
    }
  };

  if (status === 'in sale' || (status === 'ineligible' && info.length > 1)) {
    return (
      <tr className="fixture-view">
        <TableData colSpan="4">
          <div>
            {info.length > 1 && <ExtraInfo message={info} mobile />}
            {status === 'in sale' && (
              <Search>
                <PaxSelectors
                  bookingTexts={bookingTexts}
                  travelAges={travelAges}
                  handleTravelAges={handleTravelAges}
                  missingPaxAge={missingPaxAge}
                />
                <MembershipQuery>
                  {membershipRequired && (
                    <Checkbox
                      uniqueId="membership"
                      checked={isMember}
                      onChange={() => handleMembership(!isMember)}
                      className="mbm"
                    >
                      {membershipText || 'teams[team].membershipText'}
                    </Checkbox>
                  )}
                  {errorsInSearch && !isMember && membershipRequired && (
                    <span className="error">
                      {bookingTexts.membershipError || 'bookingTexts.membershipError'}
                    </span>
                  )}
                </MembershipQuery>
                <Button onClick={searchFixture} loading={isLoading} variant="primary">
                  {bookingTexts.buttonText}
                </Button>
              </Search>
            )}
            {info.length > 1 && <ExtraInfo message={info} />}
          </div>
          {status === 'in sale' && (
            <ExtraHotelNights>
              <p>{bookingTexts.hotelNights}</p>
            </ExtraHotelNights>
          )}
        </TableData>
      </tr>
    );
  }
  return [];
};

FixtureBooking.propTypes = {
  labels: PropTypes.shape({
    bookingTexts: PropTypes.shape({
      membershipError: PropTypes.string,
      buttonText: PropTypes.string,
      hotelNights: PropTypes.string,
    }).isRequired,
    teams: PropTypes.shape(),
  }).isRequired,
  fixture: PropTypes.shape({}).isRequired,
  travelAges: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleTravelAges: PropTypes.func.isRequired,
  isMember: PropTypes.bool.isRequired,
  handleMembership: PropTypes.func.isRequired,
  selectedFixture: PropTypes.string.isRequired,
  siteSettings: PropTypes.shape().isRequired,
};

export default FixtureBooking;
